/**
 * OAuth Flow Handler for Job Widget SDK
 */

import type { WidgetConfig, AuthToken, WidgetError } from './types';
import { Logger } from './logger';

export class OAuthHandler {
  private config: WidgetConfig;
  private authWindow: Window | null = null;
  private authPromise: Promise<AuthToken> | null = null;
  private authResolver: ((token: AuthToken) => void) | null = null;
  private authRejecter: ((error: WidgetError) => void) | null = null;
  private checkInterval: NodeJS.Timeout | null = null;

  constructor(config: WidgetConfig) {
    this.config = config;
    this.setupMessageListener();
    this.setupStorageListener();
    
    // Initialize logger for OAuth handler
    Logger.init({
      debug: config.debug || false,
      prefix: 'OAuthHandler'
    });
  }

  /**
   * Setup message listener for OAuth callbacks
   */
  private setupMessageListener(): void {
    window.addEventListener('message', this.handleMessage.bind(this));
  }

  /**
   * Setup storage listener to detect authentication from popup
   */
  private setupStorageListener(): void {
    window.addEventListener('storage', (event) => {
      // Listen for token being stored from popup window
      if (event.key === 'job_widget_token' && event.newValue) {
        try {
          const token = JSON.parse(event.newValue) as AuthToken;
          if (token && this.authResolver) {
            this.handleAuthSuccess(token);
          }
        } catch (err) {
          Logger.error('Failed to parse stored token', err);
        }
      }
    });
  }

  /**
   * Handle OAuth messages from popup window
   */
  private async handleMessage(event: MessageEvent): Promise<void> {
    // Accept messages from same origin (where the OAuth callback page is)
    const widgetOrigin = window.location.origin;

    // Also accept from the redirect URI origin if it's different
    const redirectOrigin = this.config.redirectUri ?
      new URL(this.config.redirectUri).origin : widgetOrigin;

    if (event.origin !== widgetOrigin && event.origin !== redirectOrigin) {
      return;
    }

    if (event.data.type === 'JOB_WIDGET_OAUTH_CALLBACK') {
      // Check if we already processed this message
      if (!this.authResolver) {
        return;
      }
      const { code, state: returnedState, error } = event.data;

      if (error) {
        Logger.error('OAuth error', error);
        this.handleAuthError({ code: 'OAUTH_ERROR', message: error, details: null });
        return;
      }

      const savedState = sessionStorage.getItem('job_widget_state');

      if (returnedState !== savedState) {
        Logger.error('OAuth state mismatch');
        this.handleAuthError({ code: 'INVALID_STATE', message: 'Invalid state parameter', details: null });
        return;
      }

      try {
        const token = await this.exchangeCodeForToken(code);
        this.handleAuthSuccess(token);
      } catch (err) {
        Logger.error('Token exchange failed', err);
        this.handleAuthError(err as WidgetError);
      }
    } else if (event.data.type === 'job-widget-oauth-success') {
      this.handleAuthSuccess(event.data.token);
    } else if (event.data.type === 'job-widget-oauth-error') {
      this.handleAuthError(event.data.error);
    }
  }

  /**
   * Initiate OAuth flow
   */
  async authenticate(): Promise<AuthToken> {
    // Check if already authenticating
    if (this.authPromise) {
      return this.authPromise;
    }

    // Check for stored token
    const storedToken = this.getStoredToken();
    if (storedToken && storedToken.expires_at > Date.now()) {
      // Only return stored token if it's still valid
      return Promise.resolve(storedToken);
    } else if (storedToken) {
      // Clear expired token
      this.clearStoredToken();
    }

    // Start new OAuth flow
    this.authPromise = new Promise((resolve, reject) => {
      this.authResolver = resolve;
      this.authRejecter = reject;

      // Generate state for CSRF protection
      const state = this.generateState();
      sessionStorage.setItem('job_widget_state', state);

      // Build OAuth URL
      const oauthUrl = this.buildOAuthUrl(state);

      // Open OAuth popup
      if (this.config.usePopup !== false) {
        this.openPopup(oauthUrl);
      } else {
        // Redirect to OAuth page
        window.location.href = oauthUrl;
      }
    });

    return this.authPromise;
  }

  /**
   * Open OAuth popup window
   */
  private openPopup(url: string): void {
    const width = 500;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    this.authWindow = window.open(
      url,
      'JobWidgetOAuth',
      `width=${width},height=${height},left=${left},top=${top},popup=yes`
    );

    if (!this.authWindow) {
      // Handle popup blocker
      this.handlePopupBlocked();
      return;
    }

    // Monitor popup
    this.monitorPopup();
  }

  /**
   * Monitor OAuth popup window
   */
  private monitorPopup(): void {
    this.checkInterval = setInterval(() => {
      if (!this.authWindow || this.authWindow.closed) {
        this.cleanup();

        // Check if auth was successful (token stored)
        const token = this.getStoredToken();
        if (token) {
          this.handleAuthSuccess(token);
        } else {
          this.handleAuthCancelled();
        }
      }
    }, 500);
  }

  /**
   * Handle popup blocker
   */
  private handlePopupBlocked(): void {
    const error: WidgetError = {
      code: 'POPUP_BLOCKED',
      message: 'Please enable popups to authenticate',
      details: {
        fallbackUrl: this.buildOAuthUrl(sessionStorage.getItem('job_widget_state') || '')
      }
    };

    if (this.authRejecter) {
      this.authRejecter(error);
    }

    this.cleanup();
  }

  /**
   * Handle authentication success
   */
  private handleAuthSuccess(token: AuthToken): void {
    // Store token
    this.storeToken(token);

    // Close popup if open
    if (this.authWindow && !this.authWindow.closed) {
      this.authWindow.close();
    }

    // Resolve promise
    if (this.authResolver) {
      this.authResolver(token);
    } else {
      Logger.warn('No auth resolver found!');
    }

    // Fire callback
    if (this.config.onAuthSuccess) {
      this.config.onAuthSuccess(token.access_token);
    }

    this.cleanup();
  }

  /**
   * Handle authentication error
   */
  private handleAuthError(error: WidgetError): void {
    if (this.authRejecter) {
      this.authRejecter(error);
    }

    if (this.config.onAuthRequired) {
      this.config.onAuthRequired();
    }

    this.cleanup();
  }

  /**
   * Handle authentication cancelled
   */
  private handleAuthCancelled(): void {
    const error: WidgetError = {
      code: 'AUTH_CANCELLED',
      message: 'Authentication was cancelled',
      details: null
    };

    if (this.authRejecter) {
      this.authRejecter(error);
    }

    this.cleanup();
  }

  /**
   * Build OAuth URL
   */
  private buildOAuthUrl(state: string): string {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.getClientId(),
      redirect_uri: this.getRedirectUri(),
      scope: 'profile:basic email',
      state: state
    });

    if (this.config.tenantId) {
      params.append('tenant_id', this.config.tenantId);
    }

    const baseUrl = this.config.baseUrl;
    return `${baseUrl}/auth/v1/oauth/authorize?${params}`;
  }

  /**
   * Get OAuth client ID
   */
  private getClientId(): string {
    return this.config.clientId || '';
  }

  /**
   * Get OAuth redirect URI
   */
  private getRedirectUri(): string {
    if (this.config.redirectUri) {
      return this.config.redirectUri;
    }
    return `${window.location.origin}/oauth-callback`;
  }

  /**
   * Exchange authorization code for access token
   */
  private async exchangeCodeForToken(code: string): Promise<AuthToken> {
    const baseUrl = this.config.baseUrl;

    const response = await fetch(`${baseUrl}/auth/v1/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        client_id: this.getClientId(),
        redirect_uri: this.getRedirectUri()
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token exchange failed: ${errorText}`);
    }

    const data = await response.json();

    return {
      access_token: data.access_token,
      expires_at: Date.now() + (data.expires_in || 3600) * 1000,
      scope: data.scope ? data.scope.split(' ') : ['profile:basic', 'email']
    };
  }

  /**
   * Generate state for CSRF protection
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) +
           Math.random().toString(36).substring(2, 15);
  }

  /**
   * Get stored token
   */
  private getStoredToken(): AuthToken | null {
    const tokenStr = sessionStorage.getItem('job_widget_token');
    if (!tokenStr) return null;

    try {
      const token = JSON.parse(tokenStr) as AuthToken;
      if (token.expires_at > Date.now()) {
        return token;
      }
      this.clearStoredToken();
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Store authentication token
   */
  private storeToken(token: AuthToken): void {
    sessionStorage.setItem('job_widget_token', JSON.stringify(token));
  }

  /**
   * Clear stored token
   */
  private clearStoredToken(): void {
    sessionStorage.removeItem('job_widget_token');
    sessionStorage.removeItem('job_widget_state');
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.authPromise = null;
    this.authResolver = null;
    this.authRejecter = null;

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }

    this.authWindow = null;
  }

  /**
   * Destroy handler
   */
  destroy(): void {
    this.cleanup();
    window.removeEventListener('message', this.handleMessage.bind(this));
  }
}